import React, { useEffect, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCol,
  CContainer,
  CImage,
  CRow,
  CAlert,
  CFormInput,
  CSpinner,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilTrash, cilPlus } from '@coreui/icons'
import devicePic2 from 'src/assets/images/female.jpeg'
import devicePic3 from 'src/assets/images/male.jpeg'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { SplashScreen } from '@capacitor/splash-screen'
import { buildApiUrl, keysToRemove } from '../../utils/getConfiguration'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'

const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

const requestJson = async (url, options = {}) => {
  const { method = 'GET', body, headers, params, ...restConfig } = options

  const response = await apiClient.request({
    url,
    method: method.toLowerCase(),
    data: body,
    params,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...restConfig,
  })

  return response.data
}

const Profile = () => {
  const [accounts, setAccounts] = useState([])
  const [visible, setVisible] = useState(false)
  const [isPartnerModalOpen, setPartnerModalOpen] = useState(false)
  const [isMailModalOpen, setMailModalOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [inputMailValue, setInputMailValue] = useState('')
  const [inputPassValue, setInputPassValue] = useState('')
  const [inputPassValue2, setInputPassValue2] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [cachedLoginEmail, setCachedLoginEmail] = useState(
    () => localStorage.getItem('loginName1') || '',
  )
  const [isMailSubmitting, setIsMailSubmitting] = useState(false)
  const [isPartnerSubmitting, setIsPartnerSubmitting] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    if (!feedback) return undefined
    const timer = window.setTimeout(() => setFeedback(null), 5000)
    return () => window.clearTimeout(timer)
  }, [feedback])

  const surfaceNetworkError = (error, message) => {
    console.error(message, error)
    setFeedback({ color: 'danger', message })
  }

  const closeMailModal = () => {
    setMailModalOpen(false)
    setInputMailValue('')
    setInputPassValue('')
    setInputPassValue2('')
  }

  const closePartnerModal = () => {
    setPartnerModalOpen(false)
    setInputValue('')
  }

  const handleMailModalSave = async () => {
    if (!isValidEmail(inputMailValue)) {
      setFeedback({ color: 'warning', message: t('profile.feedback.invalidEmail') })
      return
    }
    setIsMailSubmitting(true)
    try {
      await handleCheckExistingAccountMail(inputMailValue, inputPassValue, inputPassValue2)
    } finally {
      setIsMailSubmitting(false)
      setInputPassValue('')
      setInputPassValue2('')
    }
  }

  const handlePartnerSave = async () => {
    if (!isValidEmail(inputValue)) {
      setFeedback({ color: 'warning', message: t('profile.feedback.invalidEmail') })
      return
    }
    setIsPartnerSubmitting(true)
    try {
      await handleCheckExistingMail(inputValue)
    } finally {
      setIsPartnerSubmitting(false)
      setInputValue('')
    }
  }

  const fetchAccountsData = async () => {
    try {
      setLoading(true)
      const mail = localStorage.getItem('mail')
      const encodedParams = new URLSearchParams({
        email: mail,
      }).toString()
      const response = await apiClient.get(
        buildApiUrl('account', {
          email: mail,
        }),
      )
      setAccounts(response.data)
      setCachedLoginEmail(localStorage.getItem('loginName1') || '')
    } catch (fetchError) {
      setError(t('profile.errors.fetchAccounts'))
      console.error('Error fetching account data', fetchError)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccountsData()
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
        console.warn('Unable to hide splash screen on Profile load', splashError)
      })
    }
  }, [])

  const deleteAccount = async () => {
    try {
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('accountDelete', {
          account_ID,
        }),
      )
      console.log(localStorage.getItem('account_ID') + 'account deleted')
      setFeedback({ color: 'success', message: t('profile.feedback.accountDeleted') })
    } catch (error) {
      surfaceNetworkError(error, t('profile.feedback.accountDeleteError'))
      throw error
    }
  }

  const setTransferContact = async (contactMail) => {
    try {
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('accountsTransferContactUpdate', {
          account_ID,
          contact: contactMail,
        }),
      )
      console.log('Transfer contact update')
      fetchAccountsData()
    } catch (error) {
      surfaceNetworkError(error, t('profile.feedback.partnerUpdateError'))
      throw error
    }
  }

  const handleCheckExistingMail = async (mail) => {
    try {
      setFeedback(null)
      const jsonData = await requestJson(
        buildApiUrl('account', {
          email: mail,
        }),
      )
      const mailColumn = jsonData?.map((item) => item.mail) ?? []
      if (mailColumn.length > 0) {
        setTransferContact(mail)
        closePartnerModal()
        setVisible(false)
        setFeedback({ color: 'success', message: t('profile.feedback.partnerUpdateSuccess') })
      } else {
        setVisible(true)
        setFeedback({
          color: 'warning',
          message: t('profile.feedback.partnerEmailUnknown'),
        })
      }
    } catch (fetchError) {
      surfaceNetworkError(fetchError, t('profile.feedback.partnerVerifyError'))
      setVisible(true)
    }
  }

  const handleCheckExistingAccountMail = async (mail, pass, pass2) => {
    try {
      if (pass !== pass2) {
        setFeedback({ color: 'warning', message: t('profile.feedback.passwordsMismatch') })
        return
      }
      setFeedback(null)
      const jsonData = await requestJson(
        buildApiUrl('account', {
          email: mail,
        }),
      )
      const mailColumn = jsonData?.map((item) => item.mail) ?? []
      if (mailColumn.length > 0) {
        setFeedback({
          color: 'warning',
          message: t('profile.feedback.emailExists'),
        })
        return
      }
      throw new Error('Email not found, proceed with update')
    } catch (fetchError) {
      // If the error is our sentinel, proceed with update; otherwise surface feedback.
      const shouldProceedWithUpdate = fetchError?.message?.includes('Email not found')
      if (!shouldProceedWithUpdate) {
        console.error('Error fetching login data:', fetchError)
        setFeedback({
          color: 'danger',
          message: t('profile.feedback.emailVerifyError'),
        })
        return
      }

      try {
        await apiClient.get(
          buildApiUrl('accountNameUpdate', {
            account_ID: localStorage.getItem('account_ID'),
            mail,
            pass,
          }),
        )
        setCachedLoginEmail(mail)
        closeMailModal()
        console.log('Account mail updated')
        localStorage.setItem('loginName1', mail)
        setFeedback({ color: 'success', message: t('profile.feedback.emailUpdated') })
        navigate('/login')
      } catch (updateError) {
        surfaceNetworkError(updateError, t('profile.feedback.emailUpdateError'))
      }
    }
  }

  return (
    <>
      <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
        <CContainer className="py-4 flex-grow-1">
          <InternalAppHeader
            title={t('profile.header.title')}
            onBack={() => navigate('/dashboard', { state: { refresh: true } })}
          />

          <CRow className="justify-content-center g-4 mt-2">
            <CCol lg={10} className="d-flex flex-column gap-4">
              {feedback && (
                <CAlert
                  color={feedback.color || 'primary'}
                  dismissible
                  className="mb-0"
                  onClose={() => setFeedback(null)}
                >
                  {feedback.message}
                </CAlert>
              )}

              {error && (
                <CAlert color="danger" dismissible className="mb-0" onClose={() => setError(null)}>
                  {error}
                </CAlert>
              )}

              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <CSpinner
                    color="primary"
                    variant="grow"
                    style={{ width: '3rem', height: '3rem' }}
                    aria-label={t('profile.aria.loadingProfile')}
                  />
                </div>
              ) : (
                <CCard className="border-0 shadow-sm">
                  <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
                    <CAlert
                      color="primary"
                      dismissible
                      visible={visible}
                      className="mb-0"
                      onClose={() => setVisible(false)}
                    >
                      {t('profile.alerts.deletePrompt')}
                      <br />
                      <div className="d-flex gap-2 pt-2 flex-wrap">
                        <CButton
                          color="danger"
                          onClick={async () => {
                            if (cachedLoginEmail !== 'demo@demo.com') {
                              try {
                                await deleteAccount()
                                keysToRemove.forEach((key) => localStorage.removeItem(key))
                                navigate('/login')
                              } catch (error) {
                                // Feedback already handled in deleteAccount.
                              }
                            } else {
                              setFeedback({
                                color: 'warning',
                                message: t('profile.alerts.demoAccountBlocked'),
                              })
                            }
                          }}
                        >
                          {t('profile.actions.deleteAccount')}
                        </CButton>
                        <CButton
                          color="secondary"
                          variant="outline"
                          onClick={() => {
                            setVisible(false)
                          }}
                        >
                          {t('profile.actions.cancel')}
                        </CButton>
                      </div>
                    </CAlert>

                    <div className="small text-medium-emphasis d-flex flex-column gap-4">
                      {accounts.length > 0 ? (
                        accounts.map((account) => {
                          const avatarSrc = account.gender === 'M' ? devicePic3 : devicePic2
                          const loginEmail = cachedLoginEmail

                          return (
                            <CCard
                              key={account.id || account.account_ID}
                              className="border-0 shadow-sm"
                            >
                              <CCardBody className="p-4">
                                <CRow className="g-4 align-items-center align-items-md-start">
                                  <CCol xs={12} md={4} className="text-center">
                                    <CImage
                                      rounded
                                      src={avatarSrc}
                                      width={200}
                                      height={200}
                                      className="object-fit-cover"
                                    />
                                  </CCol>
                                  <CCol xs={12} md={8}>
                                    <div className="mb-4">
                                      <h6 className="text-uppercase text-muted fw-semibold mb-2">
                                        {t('profile.sections.accountEmail')}
                                      </h6>
                                      {isValidEmail(loginEmail) ? (
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                          <span>{loginEmail}</span>
                                          <CButton
                                            color="primary"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              setInputMailValue(loginEmail)
                                              setMailModalOpen(true)
                                            }}
                                            title={t('profile.actions.updateEmail')}
                                            aria-label={t('profile.modals.updateEmail.title')}
                                          >
                                            <CIcon icon={cilPlus} size="lg" />
                                          </CButton>
                                        </div>
                                      ) : (
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                          <span className="text-danger small">
                                            {t('profile.messages.syncEmailPrompt')}
                                          </span>
                                          <CButton
                                            color="primary"
                                            size="sm"
                                            onClick={() => {
                                              setInputMailValue('')
                                              setMailModalOpen(true)
                                            }}
                                            title={t('profile.actions.addEmail')}
                                            aria-label={t('profile.actions.addEmail')}
                                          >
                                            <CIcon icon={cilPlus} size="lg" />
                                          </CButton>
                                        </div>
                                      )}
                                    </div>

                                    <div className="border-top pt-3">
                                      <h6 className="text-uppercase text-muted fw-semibold mb-2">
                                        {t('profile.sections.quickActions')}
                                      </h6>
                                      {cachedLoginEmail !== 'demo' && (
                                        <CButton
                                          color="danger"
                                          variant="outline"
                                          className="d-inline-flex align-items-center"
                                          onClick={() => {
                                            if (cachedLoginEmail !== 'demo') {
                                              setVisible(true)
                                            }
                                          }}
                                          title={t('profile.actions.deleteAccount')}
                                          aria-label={t('profile.actions.deleteAccount')}
                                        >
                                          <CIcon icon={cilTrash} size="lg" />
                                        </CButton>
                                      )}
                                    </div>

                                    <div className="border-top pt-3 mt-3">
                                      <h6 className="text-uppercase text-muted fw-semibold mb-2">
                                        {t('profile.sections.profileInfo')}
                                      </h6>
                                      <div className="d-flex flex-column gap-1">
                                        <span>
                                          <strong>{t('profile.profileInfo.genderLabel')}:</strong>{' '}
                                          {account.gender === 'F'
                                            ? t('profile.profileInfo.female')
                                            : t('profile.profileInfo.male')}
                                        </span>
                                        <span>
                                          <strong>{t('profile.profileInfo.validityLabel')}:</strong>{' '}
                                          {account.validity}
                                        </span>
                                        <span>
                                          <strong>{t('profile.profileInfo.ageLabel')}:</strong>{' '}
                                          {t('profile.profileInfo.ageUndefined')}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="border-top pt-3 mt-3">
                                      <h6 className="text-uppercase text-muted fw-semibold mb-2">
                                        {t('profile.sections.familyMembers')}
                                      </h6>
                                      <p className="mb-2">
                                        {t('profile.family.partnerLabel')}
                                        <span className="ms-1">
                                          {account.TransferContact1 === null ||
                                          account.TransferContact1 === ''
                                            ? t('profile.family.emailMissing')
                                            : account.TransferContact1}
                                        </span>
                                      </p>
                                      <div className="d-flex flex-wrap gap-2">
                                        <CButton
                                          color="danger"
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setTransferContact('')
                                          }}
                                          title={t('profile.actions.removePartner')}
                                          aria-label={t('profile.actions.removePartner')}
                                        >
                                          <CIcon icon={cilTrash} size="lg" />
                                        </CButton>
                                        <CButton
                                          color="primary"
                                          size="sm"
                                          onClick={() => {
                                            setInputValue(account.TransferContact1 || '')
                                            setPartnerModalOpen(true)
                                          }}
                                          title={t('profile.actions.assignPartner')}
                                          aria-label={t('profile.actions.assignPartner')}
                                        >
                                          <CIcon icon={cilPlus} size="lg" />
                                        </CButton>
                                      </div>
                                    </div>
                                  </CCol>
                                </CRow>
                              </CCardBody>
                            </CCard>
                          )
                        })
                      ) : (
                        <CCardText className="alert alert-info mb-0">
                          {t('profile.messages.noAccountData')}
                        </CCardText>
                      )}
                    </div>
                  </CCardBody>
                </CCard>
              )}
            </CCol>
          </CRow>
        </CContainer>
      </div>

      <CModal visible={isMailModalOpen} onClose={closeMailModal} backdrop="static">
        <CModalHeader closeButton>
          <CModalTitle>{t('profile.modals.updateEmail.title')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CFormInput
            type="email"
            id="modalAccountEmail"
            label={t('profile.modals.updateEmail.emailLabel')}
            placeholder={t('profile.modals.updateEmail.emailPlaceholder')}
            value={inputMailValue}
            aria-describedby="modalAccountEmailHelp"
            onInput={(e) => {
              setInputMailValue(e.target.value)
            }}
          />
          <CFormInput
            type="password"
            id="modalAccountPass"
            label={t('profile.modals.updateEmail.passwordLabel')}
            placeholder={t('profile.modals.updateEmail.passwordPlaceholder')}
            className="mt-3"
            value={inputPassValue}
            aria-describedby="modalAccountPassHelp"
            onInput={(e) => {
              setInputPassValue(e.target.value)
            }}
          />
          <CFormInput
            type="password"
            id="modalAccountPassRepeat"
            label={t('profile.modals.updateEmail.passwordRepeatLabel')}
            placeholder={t('profile.modals.updateEmail.passwordRepeatPlaceholder')}
            className="mt-3"
            value={inputPassValue2}
            aria-describedby="modalAccountPassRepeatHelp"
            onInput={(e) => {
              setInputPassValue2(e.target.value)
            }}
          />
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            variant="outline"
            onClick={closeMailModal}
            disabled={isMailSubmitting}
          >
            {t('profile.actions.cancel')}
          </CButton>
          <CButton color="primary" onClick={handleMailModalSave} disabled={isMailSubmitting}>
            {isMailSubmitting ? (
              <span className="d-inline-flex align-items-center gap-2">
                <CSpinner size="sm" /> {t('profile.modals.updateEmail.saving')}
              </span>
            ) : (
              t('profile.modals.updateEmail.save')
            )}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={isPartnerModalOpen} onClose={closePartnerModal} backdrop="static">
        <CModalHeader closeButton>
          <CModalTitle>{t('profile.modals.assignPartner.title')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CFormInput
            type="email"
            id="modalPartnerEmail"
            label={t('profile.modals.assignPartner.emailLabel')}
            placeholder={t('profile.modals.assignPartner.emailPlaceholder')}
            text={t('profile.modals.assignPartner.helperText')}
            value={inputValue}
            aria-describedby="modalPartnerEmailHelp"
            onInput={(e) => {
              setInputValue(e.target.value)
            }}
          />
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            variant="outline"
            onClick={closePartnerModal}
            disabled={isPartnerSubmitting}
          >
            {t('profile.actions.cancel')}
          </CButton>
          <CButton color="primary" onClick={handlePartnerSave} disabled={isPartnerSubmitting}>
            {isPartnerSubmitting ? (
              <span className="d-inline-flex align-items-center gap-2">
                <CSpinner size="sm" /> {t('profile.modals.assignPartner.saving')}
              </span>
            ) : (
              t('profile.modals.assignPartner.save')
            )}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default Profile
